<template>
  <div class="md-layout product-form-page">
    <div class="md-layout-item">

      <md-card class="form-card">
        <md-card-header class="form-card-header">
          <div class="header-icon-wrap">
            <md-icon>add_box</md-icon>
          </div>
          <div>
            <h4 class="title">Create New Product</h4>
            <p class="subtitle">Fill in the details below to add a new product</p>
          </div>
        </md-card-header>

        <md-card-content>

          <!-- ── Step Indicators ── -->
          <div class="steps-bar">
            <div v-for="(step, i) in visibleSteps" :key="i"
              class="step-item"
              :class="{ active: currentStep === i, done: currentStep > i }"
              @click="currentStep = i"
            >
              <div class="step-circle">
                <md-icon v-if="currentStep > i">check</md-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>

          <!-- ────────── STEP 0 — Basic Info ────────── -->
          <div v-show="currentStep === 0" class="step-content">
            <h5 class="section-title">Basic Information</h5>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Product Name <span class="required">*</span></label>
                <input v-model="productDetail.name" class="field-input" placeholder="e.g. Samsung Galaxy A17 Case" />
              </div>
              <div class="form-group">
                <label class="field-label">Display Name <span class="required">*</span></label>
                <input v-model="productDetail.displayName" class="field-input" placeholder="Shown to customers" />
              </div>
            </div>

            <div class="form-group">
              <label class="field-label">Short Description <span class="required">*</span></label>
              <textarea v-model="productDetail.shortDescription" class="field-input field-textarea" rows="3" placeholder="Brief description…" />
            </div>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Product Type <span class="required">*</span></label>
                <select v-model="productDetail.productType" class="field-input field-select" @change="onTypeChange">
                  <option value="Onetime">One-time</option>
                  <option value="Subscription">Subscription</option>
                  <option value="Lifetime Subscription">Lifetime Subscription</option>
                  <option value="Accessories">Accessories</option>
                </select>
              </div>

              <!-- Recurring interval (Subscription) -->
              <div class="form-group" v-if="productDetail.productType === 'Subscription'">
                <label class="field-label">Recurring Interval <span class="required">*</span></label>
                <select v-model="productDetail.recurringInterval" class="field-input field-select">
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>

              <!-- Category (non-accessories) -->
              <div class="form-group" v-if="productDetail.productType !== 'Accessories'">
                <label class="field-label">Product Category <span class="required">*</span></label>
                <select v-model="productDetail.productCategory" class="field-input field-select">
                  <option value="">— select —</option>
                  <option value="Phones">Phones</option>
                  <option value="Tablets">Tablets</option>
                </select>
              </div>

              <!-- Category (accessories — optional) -->
              <div class="form-group" v-if="productDetail.productType === 'Accessories'">
                <label class="field-label">Product Category <span class="optional">(optional)</span></label>
                <select v-model="productDetail.productCategory" class="field-input field-select">
                  <option value="">— select —</option>
                  <option value="Phones">Phones</option>
                  <option value="Tablets">Tablets</option>
                </select>
              </div>
            </div>

            <!-- GTIN -->
            <div class="form-row two-col" v-if="productDetail.productType !== 'Accessories'">
              <div class="form-group">
                <label class="field-label">GTIN</label>
                <input v-model="productDetail.gtin" class="field-input" placeholder="13-digit barcode" />
              </div>
            </div>
          </div>

          <!-- ────────── STEP 1 — Pricing (all product types) ────────── -->
          <div v-show="currentStep === 1" class="step-content">
            <h5 class="section-title">Pricing</h5>

            <div class="form-row three-col">
              <div class="form-group">
                <label class="field-label">Price (GBP) <span class="required">*</span></label>
                <div class="input-with-prefix">
                  <span class="input-prefix">£</span>
                  <input v-model.number="productDetail.price" type="number" min="0" step="0.01" class="field-input prefix-input" placeholder="0.00" />
                </div>
              </div>
              <div class="form-group">
                <label class="field-label">Offer Price (GBP)</label>
                <div class="input-with-prefix">
                  <span class="input-prefix">£</span>
                  <input v-model.number="productDetail.offerPrice" type="number" min="0" step="0.01" class="field-input prefix-input" placeholder="0.00" />
                </div>
              </div>
              <div class="form-group">
                <label class="field-label">Offer Price Label</label>
                <input v-model="productDetail.offerPriceText" class="field-input" placeholder="e.g. Limited time!" />
              </div>
            </div>
          </div>

          <!-- ────────── STEP 2 — Compatible Products (accessories only) ────────── -->
          <div v-if="productDetail.productType === 'Accessories'" v-show="currentStep === 2" class="step-content">
            <h5 class="section-title">Compatible Products</h5>
            <p class="section-desc">Select which one-time products this accessory belongs to.</p>

            <div v-if="onetimeProducts.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon> No one-time products found. Create some first.
            </div>

            <div class="parent-products-grid">
              <label
                v-for="p in onetimeProducts"
                :key="p._id"
                class="parent-product-card"
                :class="{ selected: productDetail.parentProductIds.includes(p._id) }"
              >
                <input
                  type="checkbox"
                  :value="p._id"
                  v-model="productDetail.parentProductIds"
                  style="display:none"
                />
                <div class="pp-avatar">{{ (p.name || '?')[0].toUpperCase() }}</div>
                <div class="pp-info">
                  <strong>{{ p.displayName || p.name }}</strong>
                  <small>{{ p.name }}</small>
                </div>
                <md-icon class="pp-check">{{ productDetail.parentProductIds.includes(p._id) ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
              </label>
            </div>
          </div>

          <!-- ────────── IMAGE STEP ────────── -->
          <div v-show="currentStep === imageStep" class="step-content">
            <h5 class="section-title">{{ productDetail.productType === 'Accessories' ? 'Main Product Image (optional)' : 'Main Product Image' }}</h5>

            <div class="image-upload-area" @click="triggerMainImageInput" @dragover.prevent @drop.prevent="onMainImageDrop">
              <div v-if="mainImagePreview" class="image-upload-preview">
                <img :src="mainImagePreview" class="preview-img" />
                <button class="remove-preview" @click.stop="removeMainImage"><md-icon>close</md-icon></button>
              </div>
              <div v-else class="upload-placeholder">
                <md-icon class="upload-cloud-icon">cloud_upload</md-icon>
                <p>Click or drag & drop to upload main image</p>
                <small>PNG, JPG, WEBP up to 5MB</small>
              </div>
              <input ref="mainImageInput" type="file" accept="image/*" @change="handleMainImageUpload" style="display:none" />
            </div>
          </div>

          <!-- ────────── SPECS STEP ────────── -->
          <div v-show="currentStep === specsStep" class="step-content">
            <h5 class="section-title">Product Specifications</h5>
            <p class="section-desc">Add key–value specifications (e.g. Battery → 5000 mAh).</p>

            <div v-if="productDetail.productSpecifications.length === 0" class="empty-specs">
              <md-icon>list_alt</md-icon>
              <p>No specifications yet</p>
            </div>

            <div v-for="(spec, i) in productDetail.productSpecifications" :key="i" class="spec-row">
              <input v-model="spec.key" class="field-input spec-input" placeholder="Key (e.g. Battery)" />
              <input v-model="spec.value" class="field-input spec-input" placeholder="Value (e.g. 5000 mAh)" />
              <button class="remove-spec-btn" @click="removeSpecification(i)">
                <md-icon>delete_outline</md-icon>
              </button>
            </div>

            <button class="add-spec-btn" @click="addSpecification">
              <md-icon>add</md-icon> Add Specification
            </button>
          </div>

          <!-- ── Nav Buttons ── -->
          <div class="form-nav">
            <button class="nav-btn btn-back" @click="currentStep--" v-if="currentStep > 0">
              <md-icon>arrow_back</md-icon> Back
            </button>
            <div class="nav-spacer" />
            <button class="nav-btn btn-next" @click="currentStep++" v-if="currentStep < visibleSteps.length - 1">
              Next <md-icon>arrow_forward</md-icon>
            </button>
            <button class="nav-btn btn-submit" @click="createProduct" :disabled="isDisabled" v-if="currentStep === visibleSteps.length - 1">
              <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i> Creating…</span>
              <span v-else><md-icon>check</md-icon> Create Product</span>
            </button>
          </div>

        </md-card-content>
      </md-card>

    </div>
  </div>
</template>

<script>
import OrdersDataService from '../../services/SuperAdmin/Kids/OrdersDataService';

const STEPS_STANDARD = ['Basic Info', 'Pricing', 'Image', 'Specifications'];
const STEPS_ACCESSORIES = ['Basic Info', 'Pricing', 'Compatible Products', 'Image', 'Specifications'];

export default {
  data() {
    return {
      currentStep: 0,
      isDisabled: false,
      mainImageFile: null,
      mainImagePreview: null,
      onetimeProducts: [],
      productDetail: {
        name: '',
        displayName: '',
        shortDescription: '',
        productType: 'Onetime',
        productCategory: '',
        gtin: '',
        price: null,
        offerPrice: null,
        offerPriceText: '',
        recurringInterval: '',
        productSpecifications: [],
        mainImage: '',
        // accessories
        parentProductIds: [],
      },
    };
  },

  computed: {
    visibleSteps() {
      return this.productDetail.productType === 'Accessories' ? STEPS_ACCESSORIES : STEPS_STANDARD;
    },
    // Step indexes vary by type: standard = [0,1,2,3], accessories = [0,1,2,3,4]
    imageStep() {
      return this.productDetail.productType === 'Accessories' ? 3 : 2;
    },
    specsStep() {
      return this.productDetail.productType === 'Accessories' ? 4 : 3;
    },
  },

  methods: {
    onTypeChange() {
      this.currentStep = 0;
      if (this.productDetail.productType === 'Accessories' && this.onetimeProducts.length === 0) {
        this.loadOnetimeProducts();
      }
    },
    loadOnetimeProducts() {
      OrdersDataService.getOnetimeProducts()
        .then((r) => { this.onetimeProducts = r.data.data; })
        .catch(() => {});
    },
    triggerMainImageInput() {
      this.$refs.mainImageInput.click();
    },
    handleMainImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.mainImageFile = file;
      this.mainImagePreview = URL.createObjectURL(file);
    },
    onMainImageDrop(e) {
      const file = e.dataTransfer.files[0];
      if (!file || !file.type.startsWith('image/')) return;
      this.mainImageFile = file;
      this.mainImagePreview = URL.createObjectURL(file);
    },
    removeMainImage() {
      this.mainImageFile = null;
      this.mainImagePreview = null;
      this.productDetail.mainImage = '';
    },
    addSpecification() {
      this.productDetail.productSpecifications.push({ key: '', value: '' });
    },
    removeSpecification(i) {
      this.productDetail.productSpecifications.splice(i, 1);
    },
    async createProduct() {
      if (!this.productDetail.name.trim() || !this.productDetail.displayName.trim()) {
        this.$toast.error('Name and Display Name are required.');
        this.currentStep = 0;
        return;
      }
      if (!this.productDetail.price || this.productDetail.price <= 0) {
        this.$toast.error('A valid price is required.');
        this.currentStep = 1;
        return;
      }

      this.isDisabled = true;

      // Upload main image first (if provided)
      if (this.mainImageFile) {
        try {
          const fd = new FormData();
          fd.append('images[]', this.mainImageFile);
          const uploadRes = await OrdersDataService.uploadWingioProductImages(fd);
          const uploaded = uploadRes.data?.data?.[0];
          if (uploaded?.productImage) {
            this.productDetail.mainImage = uploaded.productImage;
          }
        } catch (e) {
          this.$toast.error('Image upload failed.');
          this.isDisabled = false;
          return;
        }
      }

      OrdersDataService.createProduct(this.productDetail)
        .then(() => {
          this.$toast.success('Product created successfully!');
          this.$router.push('/kid/wingio-products');
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.respones_msg || 'Create failed.');
          this.isDisabled = false;
        });
    },
  },

  beforeMount() {
    // Pre-load onetime products in case user switches to Accessories
    this.loadOnetimeProducts();
  },
};
</script>

<style scoped>
.product-form-page { font-family: 'Segoe UI', sans-serif; }

.form-card { border-radius: 14px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }

/* Header */
.form-card-header {
  display: flex; align-items: center; gap: 16px;
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #43a047 0%, #1b5e20 100%);
  color: white;
}
.header-icon-wrap {
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.header-icon-wrap .md-icon { color: white !important; font-size: 26px !important; }
.form-card-header .title { color: white; margin: 0; font-size: 20px; font-weight: 600; }
.form-card-header .subtitle { color: rgba(255,255,255,0.75); margin: 4px 0 0; font-size: 13px; }

/* Steps bar */
.steps-bar {
  display: flex; align-items: center;
  padding: 20px 28px 0;
  gap: 0;
}
.step-item {
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; flex: 1; position: relative;
}
.step-item:not(:last-child)::after {
  content: ''; flex: 1; height: 2px;
  background: #e0e0e0; margin: 0 8px;
}
.step-item.done:not(:last-child)::after { background: #43a047; }
.step-circle {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #ddd; background: white;
  font-size: 13px; font-weight: 700; color: #aaa;
  flex-shrink: 0; transition: all 0.2s;
}
.step-item.active .step-circle { border-color: #43a047; background: #43a047; color: white; }
.step-item.done .step-circle { border-color: #43a047; background: #e8f5e9; color: #43a047; }
.step-item.active .step-circle .md-icon,
.step-item.done .step-circle .md-icon { color: inherit !important; font-size: 16px !important; }
.step-label { font-size: 12px; font-weight: 500; color: #aaa; white-space: nowrap; }
.step-item.active .step-label { color: #43a047; font-weight: 600; }
.step-item.done .step-label { color: #555; }

/* Step content */
.step-content { padding: 24px 28px 8px; min-height: 280px; }
.section-title { font-size: 15px; font-weight: 600; color: #333; margin: 0 0 6px; }
.section-desc { font-size: 13px; color: #888; margin: 0 0 20px; }

/* Form elements */
.form-row { display: flex; gap: 16px; }
.form-row.two-col > * { flex: 1; }
.form-row.three-col > * { flex: 1; }
.form-group { margin-bottom: 18px; }
.field-label { display: block; font-size: 12px; font-weight: 600; color: #555; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.4px; }
.required { color: #e53935; }
.optional { color: #aaa; font-weight: 400; text-transform: none; letter-spacing: 0; }
.field-input {
  width: 100%; padding: 9px 12px; border: 1.5px solid #e0e0e0;
  border-radius: 8px; font-size: 14px; color: #333;
  outline: none; transition: border-color 0.2s; box-sizing: border-box;
  background: white;
}
.field-input:focus { border-color: #43a047; }
.field-textarea { resize: vertical; min-height: 80px; font-family: inherit; }
.field-select { -webkit-appearance: none; background: white; cursor: pointer; }

/* Price inputs */
.input-with-prefix { position: relative; }
.input-prefix {
  position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
  color: #888; font-size: 14px; pointer-events: none;
}
.prefix-input { padding-left: 26px; }

/* Parent products (accessories) */
.info-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; background: #e3f2fd; border-radius: 8px;
  font-size: 14px; color: #1565c0; margin-bottom: 16px;
}
.parent-products-grid { display: flex; flex-direction: column; gap: 8px; max-height: 360px; overflow-y: auto; }
.parent-product-card {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; border: 1.5px solid #e0e0e0; border-radius: 10px;
  cursor: pointer; transition: all 0.15s; background: white;
}
.parent-product-card:hover { border-color: #9c27b0; background: #fdf3ff; }
.parent-product-card.selected { border-color: #9c27b0; background: #f3e5f5; }
.pp-avatar {
  width: 38px; height: 38px; border-radius: 9px;
  background: #4caf50; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 700; flex-shrink: 0;
}
.pp-info { flex: 1; }
.pp-info strong { display: block; font-size: 14px; color: #333; }
.pp-info small { color: #888; font-size: 12px; }
.pp-check { color: #9c27b0 !important; font-size: 20px !important; }

/* Image upload */
.image-upload-area {
  border: 2px dashed #ccc; border-radius: 12px;
  min-height: 200px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s; overflow: hidden; position: relative;
}
.image-upload-area:hover { border-color: #43a047; background: #f9fff9; }
.upload-placeholder { text-align: center; padding: 40px; }
.upload-cloud-icon { font-size: 48px !important; color: #ccc !important; margin-bottom: 8px; display: block; }
.upload-placeholder p { color: #888; margin: 8px 0 4px; }
.upload-placeholder small { color: #bbb; }
.image-upload-preview { position: relative; }
.preview-img { max-height: 200px; max-width: 100%; border-radius: 8px; display: block; }
.remove-preview {
  position: absolute; top: 8px; right: 8px;
  background: rgba(0,0,0,0.5); border: none; border-radius: 50%;
  width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: white;
}
.remove-preview .md-icon { color: white !important; font-size: 16px !important; }

/* Specs */
.empty-specs { text-align: center; padding: 30px; color: #ccc; }
.empty-specs .md-icon { font-size: 36px !important; color: #ddd !important; display: block; margin-bottom: 8px; }
.spec-row { display: flex; gap: 12px; align-items: center; margin-bottom: 10px; }
.spec-input { flex: 1; }
.remove-spec-btn {
  background: none; border: none; cursor: pointer; padding: 6px;
  color: #e53935; border-radius: 6px; transition: background 0.15s;
}
.remove-spec-btn:hover { background: #ffebee; }
.remove-spec-btn .md-icon { color: #e53935 !important; font-size: 18px !important; }
.add-spec-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border: 1.5px dashed #43a047;
  background: transparent; border-radius: 8px;
  color: #43a047; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.add-spec-btn:hover { background: #e8f5e9; }
.add-spec-btn .md-icon { color: #43a047 !important; font-size: 18px !important; }

/* Nav */
.form-nav {
  display: flex; align-items: center; padding: 20px 28px 28px;
  border-top: 1px solid #f0f0f0; margin-top: 16px;
}
.nav-spacer { flex: 1; }
.nav-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 22px; border-radius: 9px; font-size: 14px; font-weight: 600;
  border: none; cursor: pointer; transition: all 0.15s;
}
.btn-back { background: #f5f5f5; color: #555; }
.btn-back:hover { background: #eee; }
.btn-back .md-icon { color: #555 !important; font-size: 18px !important; }
.btn-next { background: #e8f5e9; color: #2e7d32; }
.btn-next:hover { background: #c8e6c9; }
.btn-next .md-icon { color: #2e7d32 !important; font-size: 18px !important; }
.btn-submit { background: #43a047; color: white; }
.btn-submit:hover:not(:disabled) { background: #388e3c; }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
.btn-submit .md-icon { color: white !important; font-size: 18px !important; }
</style>